"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@clerk/nextjs";

interface SubscriptionStatus {
    plan: "free" | "pro";
    status: "active" | "trialing" | "past_due" | "canceled" | "canceling" | null;
    subscription_id: string | null;
    period_end: string | null;
    is_pro: boolean;
    is_canceling?: boolean;
}

interface SubscriptionContextType {
    isLoading: boolean;
    isPro: boolean;
    plan: "free" | "pro";
    subscriptionStatus: SubscriptionStatus["status"];
    periodEnd: string | null;
    subscriptionId: string | null;
    isCanceling: boolean;
    refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
    const { isSignedIn, isLoaded } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [subscriptionData, setSubscriptionData] = useState<SubscriptionStatus | null>(null);

    const fetchSubscriptionStatus = useCallback(async () => {
        if (!isSignedIn) {
            setSubscriptionData(null);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const response = await api.get<SubscriptionStatus>("/api/subscription/status");
            setSubscriptionData(response.data);
        } catch (error) {
            console.error("Failed to fetch subscription status:", error);
            setSubscriptionData({
                plan: "free",
                status: null,
                subscription_id: null,
                period_end: null,
                is_pro: false,
            });
        } finally {
            setIsLoading(false);
        }
    }, [isSignedIn]);

    useEffect(() => {
        if (isLoaded) {
            fetchSubscriptionStatus();
        }
    }, [isLoaded, fetchSubscriptionStatus]);

    const value: SubscriptionContextType = {
        isLoading,
        isPro: subscriptionData?.is_pro ?? false,
        plan: subscriptionData?.plan ?? "free",
        subscriptionStatus: subscriptionData?.status ?? null,
        periodEnd: subscriptionData?.period_end ?? null,
        subscriptionId: subscriptionData?.subscription_id ?? null,
        isCanceling: subscriptionData?.is_canceling ?? subscriptionData?.status === "canceling",
        refetch: fetchSubscriptionStatus,
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
}

export function useSubscription() {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error("useSubscription must be used within a SubscriptionProvider");
    }
    return context;
}
